import { useCallback, useState } from 'react'
import { HardDrive, Server, FolderOpen } from 'lucide-react'
import AdminScreenShell, { Tegel, StatusRij } from '@/components/AdminScreenShell'
import { adminAPI } from '@/lib/api'

const OK = '#61E294', WRN = '#FFB57A', ERR = '#FF5A5A'

const gb = (b) => b == null ? '—' : `${(Number(b) / 1e9).toFixed(1)} GB`
const tb = (b) => b == null ? '—' : `${(Number(b) / 1e12).toFixed(2)} TB`

export default function AdminStorageScreen() {
  const [d, setD] = useState(null)
  const load = useCallback(async () => {
    const r = await adminAPI.schermStorage(); setD(r.data)
  }, [])
  const pools = d?.pools ?? []
  const shares = d?.shares ?? []
  const disks = d?.lokaleDisks ?? []
  const uploadsBytes = d?.uploadsGroei
  const criticalPool = pools.some(p => p.pct >= 90 || p.healthy === false)
  const criticalDisk = disks.some(d => d.pct >= 90)

  const overall = criticalPool || criticalDisk ? ERR
                : pools.some(p => p.pct >= 80) || disks.some(d => d.pct >= 80) ? WRN
                : OK

  return (
    <AdminScreenShell title="Storage" subtitle="TrueNAS pools · SMB-shares · lokale disks"
                      icon={HardDrive} accent={overall} refreshMs={30_000} onRefresh={load}>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Tegel label="Pools" waarde={pools.length}
               sub={`${pools.filter(p => p.healthy).length} healthy`}
               kleur={pools.every(p => p.healthy) ? OK : ERR} groot />
        <Tegel label="SMB-shares" waarde={shares.length}
               sub={`${shares.filter(s => s.enabled).length} enabled`} kleur="#B4FF39" />
        <Tegel label="Uploads-dir"
               waarde={gb(uploadsBytes)}
               sub="/app/uploads" kleur="#B4FF39" />
        <Tegel label="Lokale disks" waarde={disks.length}
               sub={`${disks.filter(d => d.pct < 80).length} onder 80%`} kleur={overall} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-[6px] border border-border bg-card p-4">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Server className="h-3 w-3 inline mr-1" /> TrueNAS pools
          </h2>
          {pools.length === 0 ? (
            <div className="text-muted-foreground text-sm">
              Geen pool-data. Zet <code className="text-xs">TRUENAS_API_KEY</code> in env om deze aan te vullen.
            </div>
          ) : pools.map(p => (
            <StatusRij key={p.name}
              naam={p.name}
              sub={`${tb(p.allocated)} / ${tb(p.size)} · ${p.status ?? '?'}`}
              status={p.pct != null ? `${p.pct}%` : '—'}
              kleur={p.pct >= 90 || !p.healthy ? ERR : p.pct >= 80 ? WRN : OK} />
          ))}

          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mt-4 mb-2">
            <FolderOpen className="h-3 w-3 inline mr-1" /> SMB-shares
          </h2>
          {shares.length === 0 ? (
            <div className="text-muted-foreground text-sm">Geen shares zichtbaar.</div>
          ) : shares.map(s => (
            <StatusRij key={s.name} naam={s.name} sub={s.path}
                       status={s.enabled ? 'enabled' : 'disabled'}
                       kleur={s.enabled ? OK : WRN} />
          ))}
        </div>

        <div className="rounded-[6px] border border-border bg-card p-4">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <HardDrive className="h-3 w-3 inline mr-1" /> Lokale disks (CT200)
          </h2>
          {disks.length === 0 ? (
            <div className="text-muted-foreground text-sm">Geen disk-data.</div>
          ) : disks.map(d => (
            <StatusRij key={d.mount} naam={d.mount}
                       sub={`${d.used_gb} / ${d.total_gb} GB · ${d.device}`}
                       status={`${d.pct}%`}
                       kleur={d.pct >= 90 ? ERR : d.pct >= 80 ? WRN : OK} />
          ))}
        </div>
      </div>
    </AdminScreenShell>
  )
}
